(() => {
  const BADGE_CONTAINER_CLASS = 'yk-smart-cart-inline-badges';

  const badgesConfig = {
    subscription: { background: '#E1EFFF', color: '#111111' },
    compareAtPromo: { background: '#FECCE1', color: '#111111' },
    discountCode: { background: '#DD1267', color: '#FFFFFF' },
    bundle: { background: '#FFDFEF', color: '#111111' },
    bundleLevels: {
      5: { background: '#FFDFEF', color: '#000000' },
      10: { background: '#FF87C4', color: '#000000' },
      15: { background: '#DD1267', color: '#FFFFFF' },
    },
    multipackLevels: {
      10: { background: '#FECCE1', color: '#111111' },
      15: { background: '#DD1267', color: '#FFFFFF' },
    },
    gift: { label: 'PREZENT', background: '#DD1267', color: '#FFFFFF' },
  };

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseMoneyValue = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') return 0;

    const normalized = value
      .replace(/[^\d,.-]/g, '')
      .replace(',', '.')
      .trim();
    if (!normalized) return 0;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseCompareAtStringToCents = (compareAtPrice) => {
    if (typeof compareAtPrice !== 'string') return null;

    const normalized = compareAtPrice.replace(',', '.').trim();
    if (!normalized) return null;

    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount <= 0) return null;

    return Math.round(amount * 100);
  };

  const getItemVariant = (item) => {
    const variants = item?.product?.variants;
    if (!Array.isArray(variants) || variants.length === 0) return null;

    const variantId = toNumber(item?.variant_id);
    const variant = variants.find((currentVariant) => toNumber(currentVariant?.id) === variantId);
    return variant || null;
  };

  const formatPercent = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return null;

    return Number.isInteger(numericValue) ? String(numericValue) : String(numericValue).replace('.', ',');
  };

  const getGroupPercentFromName = (groupName) => {
    const match = String(groupName || '').match(/(\d+(?:[.,]\d+)?)%/);
    if (!match?.[1]) return 0;

    return toNumber(match[1].replace(',', '.'));
  };

  const getGroupPercent = (group) => {
    if (!group) return 0;

    const groupPercent =
      String(group?.discount_type || '').toLowerCase() === 'percentage' ? toNumber(group?.discount_amount) : 0;
    if (groupPercent > 0) return groupPercent;

    if (Array.isArray(group?.selling_plans)) {
      const highestPlanPercent = group.selling_plans.reduce((highestPercent, plan) => {
        const planPercent = toNumber(plan?.price_adjustments?.[0]?.value);
        return planPercent > highestPercent ? planPercent : highestPercent;
      }, 0);

      if (highestPlanPercent > 0) return highestPlanPercent;
    }

    return getGroupPercentFromName(group?.name);
  };

  const getSubscriptionPercent = (item) => {
    if (!item?.selling_plan_allocation) return null;

    const selectedPlanId = toNumber(item?.selling_plan_allocation?.selling_plan?.id);
    const sellingPlanGroups = item?.product?.selling_plan_groups;

    if (!Array.isArray(sellingPlanGroups) || sellingPlanGroups.length === 0) return null;

    let matchedGroup = null;

    for (const group of sellingPlanGroups) {
      const groupPercent = getGroupPercent(group);

      if (!Array.isArray(group?.selling_plans) || group.selling_plans.length === 0) {
        if (groupPercent > 0) return groupPercent;
        continue;
      }

      const matchedPlan = group.selling_plans.find((plan) => toNumber(plan?.id) === selectedPlanId);

      if (matchedPlan) {
        matchedGroup = group;
        break;
      }
    }

    if (matchedGroup) {
      const matchedGroupPercent = getGroupPercent(matchedGroup);

      if (matchedGroupPercent > 0) return matchedGroupPercent;
    }

    const secondGroup = sellingPlanGroups[1] || null;
    if (secondGroup) {
      const secondGroupPercent = getGroupPercent(secondGroup);

      if (secondGroupPercent > 0) return secondGroupPercent;
    }

    const fallbackGroupPercent = sellingPlanGroups.reduce((highestPercent, group) => {
      const groupPercent = getGroupPercent(group);
      return groupPercent > highestPercent ? groupPercent : highestPercent;
    }, 0);

    return fallbackGroupPercent || null;
  };

  const getPromoPercentFromCompareAt = (item) => {
    const variant = getItemVariant(item);
    if (!variant) return null;

    const compareAtLinePrice = parseCompareAtStringToCents(variant.compare_at_price);
    if (!compareAtLinePrice) return null;

    const quantity = Math.max(1, toNumber(item?.quantity));
    const compareAtTotal = compareAtLinePrice * quantity;
    const finalLinePrice = toNumber(item?.final_line_price);

    if (!compareAtTotal || finalLinePrice <= 0 || finalLinePrice >= compareAtTotal) return null;

    const percent = Math.round(((compareAtTotal - finalLinePrice) / compareAtTotal) * 100);
    if (!Number.isFinite(percent) || percent <= 0) return null;

    return percent;
  };

  const getDiscountCodeBadge = (item) => {
    if (!Array.isArray(item?.discounts) || item.discounts.length === 0) return null;

    const firstDiscountWithTitle = item.discounts.find((discount) => discount?.title);
    if (!firstDiscountWithTitle?.title) return null;

    const normalizedTitle = String(firstDiscountWithTitle.title).trim();
    if (!normalizedTitle) return null;

    const trailingNumberMatch = normalizedTitle.match(/^[^\d]+(\d+(?:[.,]\d+)?)%?$/);
    if (trailingNumberMatch?.[1]) {
      return {
        label: `KOD -${trailingNumberMatch[1].replace(',', '.')}%`,
        isTitleFallback: false,
      };
    }

    return {
      label: normalizedTitle,
      isTitleFallback: true,
    };
  };

  const hasTwoPlusOneOrOnePlusOneDiscount = (item) => {
    if (!Array.isArray(item?.discounts) || item.discounts.length === 0) return false;

    return item.discounts.some((discount) => {
      const title = String(discount?.title || '').toLowerCase();
      return title.includes('2+1') || title.includes('1+1');
    });
  };

  const getItemPropertyValue = (item, key) => {
    const properties = item?.properties;
    if (!properties || !key) return null;

    if (Array.isArray(properties)) {
      const found = properties.find((entry) => {
        const entryKey = String(entry?.name || entry?.key || '').trim();
        return entryKey === key;
      });
      return found?.value ?? null;
    }

    if (typeof properties === 'object') {
      return properties[key] ?? null;
    }

    return null;
  };

  const getBundleBadge = (item) => {
    if (!item?.has_components) return null;

    const discountLevelRaw = getItemPropertyValue(item, '_discountLevel');
    const discountLevel = String(discountLevelRaw || '').trim();
    if (!discountLevel) {
      return {
        label: 'ZESTAW',
        background: badgesConfig.bundle.background,
        color: badgesConfig.bundle.color,
      };
    }

    const normalizedLevel = discountLevel.replace(/\s+/g, '');
    const levelStyle = badgesConfig.bundleLevels[normalizedLevel] || {
      background: badgesConfig.bundle.background,
      color: badgesConfig.bundle.color,
    };

    return {
      label: `ZESTAW -${normalizedLevel}%`,
      background: levelStyle.background,
      color: levelStyle.color,
    };
  };

  const hasGiftProperties = (item) => {
    const properties = item?.properties;
    if (!properties) return false;

    if (Array.isArray(properties)) {
      const keys = properties.map((entry) => String(entry?.name || entry?.key || '')).filter(Boolean);
      return keys.includes('_gift_for') && keys.includes('_gift_quantity');
    }

    if (typeof properties === 'object') {
      return (
        Object.prototype.hasOwnProperty.call(properties, '_gift_for') &&
        Object.prototype.hasOwnProperty.call(properties, '_gift_quantity')
      );
    }

    return false;
  };

  const isGiftItem = (item, itemElement) => {
    if (itemElement?.classList?.contains('tag-gratis_product')) return true;
    return hasGiftProperties(item);
  };

  const createBadge = ({ label, background, color }) => {
    const badge = document.createElement('span');
    badge.className = 'yk-custom-widget-badge yk-cart-badge-padding';
    badge.textContent = label;
    badge.style.backgroundColor = background;
    badge.style.color = color;
    badge.style.borderRadius = '9999px';
    badge.style.display = 'inline-flex';
    badge.style.alignItems = 'center';
    badge.style.justifyContent = 'center';
    badge.style.fontSize = '10px';
    badge.style.fontWeight = '500';
    badge.style.padding = '4px 6px';
    badge.style.height = '16px';
    badge.style.whiteSpace = 'nowrap';
    badge.style.textTransform = 'uppercase';
    badge.style.lineHeight = '1';
    return badge;
  };

  const getOrCreateBadgeContainer = (itemElement) => {
    const infoSecond = itemElement.querySelector('.rebuy-cart__flyout-item-info-second');
    const priceElement = infoSecond?.querySelector('.rebuy-cart__flyout-item-price');
    const removeButton = infoSecond?.querySelector('.rebuy-cart__flyout-item-remove');
    if (!infoSecond || !priceElement || !removeButton) return null;

    let container = infoSecond.querySelector(`.${BADGE_CONTAINER_CLASS}`);

    if (!container) {
      container = document.createElement('div');
      container.className = BADGE_CONTAINER_CLASS;
      container.style.display = 'flex';
      infoSecond.insertBefore(container, removeButton);
    }

    return container;
  };

  const buildBadgeForItem = (item, itemElement) => {
    if (isGiftItem(item, itemElement)) {
      return {
        label: badgesConfig.gift.label,
        background: badgesConfig.gift.background,
        color: badgesConfig.gift.color,
      };
    }

    if (hasTwoPlusOneOrOnePlusOneDiscount(item)) {
      return {
        label: badgesConfig.gift.label,
        background: badgesConfig.gift.background,
        color: badgesConfig.gift.color,
      };
    }

    const discountBadge = getDiscountCodeBadge(item);
    if (discountBadge?.label) {
      return {
        label: discountBadge.label,
        background: badgesConfig.discountCode.background,
        color: badgesConfig.discountCode.color,
      };
    }

    const bundleBadge = getBundleBadge(item);
    if (bundleBadge) {
      return {
        label: bundleBadge.label,
        background: bundleBadge.background,
        color: bundleBadge.color,
      };
    }

    const multipackDiscount = window.YkMultipackDiscount?.get(item);
    if (multipackDiscount?.percent) {
      const level = badgesConfig.multipackLevels[multipackDiscount.percent] || badgesConfig.discountCode;
      return {
        label: `ZESTAW -${multipackDiscount.percent}%`,
        background: level.background,
        color: level.color,
      };
    }

    const promoPercent = getPromoPercentFromCompareAt(item);
    if (promoPercent) {
      return {
        label: `PROMO -${promoPercent}%`,
        background: badgesConfig.compareAtPromo.background,
        color: badgesConfig.compareAtPromo.color,
      };
    }

    if (item?.selling_plan_allocation) {
      const subscriptionPercent = formatPercent(getSubscriptionPercent(item));
      return {
        label: subscriptionPercent ? `-${subscriptionPercent}% SUB` : 'SUB',
        background: badgesConfig.subscription.background,
        color: badgesConfig.subscription.color,
      };
    }

    return null;
  };

  const renderBadgesForItemElement = (item, itemElement) => {
    const container = getOrCreateBadgeContainer(itemElement);
    if (!container) return;

    const badge = buildBadgeForItem(item, itemElement);
    container.innerHTML = '';

    if (!badge) {
      container.remove();
      return;
    }

    container.appendChild(createBadge(badge));
  };

  const renderSmartCartBadges = () => {
    const items = window.Rebuy?.SmartCart?.cart?.items;
    if (!Array.isArray(items) || items.length === 0) return;

    items.forEach((item) => {
      const key = item?.key;
      if (!key) return;

      const itemElements = document.querySelectorAll(`.rebuy-cart__flyout-item[data-item-key="${key}"]`);
      itemElements.forEach((itemElement) => renderBadgesForItemElement(item, itemElement));
    });
  };

  const scheduleRender = () => {
    window.requestAnimationFrame(() => {
      setTimeout(renderSmartCartBadges, 30);
    });
  };

  document.addEventListener('rebuy:cart.ready', scheduleRender);
  document.addEventListener('rebuy:cart.change', scheduleRender);
  document.addEventListener('rebuy:cart.enriched', scheduleRender);
  document.addEventListener('rebuy:smartcart.show', scheduleRender);
})();
